import { z } from "zod";
import { PROPOSAL_STATUSES } from "./common";

/** PO Confirmation Input filter — field names verbatim from
 * GSP PO Confirmation Process Technical Design §4.1.3. */
export const poFilterSchema = z.object({
  vendorId: z.coerce.number().int().optional(),
  plantId: z.coerce.number().int().optional(),
  materialNumber: z.string().optional(),
  fromDate: z.string().optional(),
  toDate: z.string().optional(),
  poNumber: z.string().optional(),
  olderThanDays: z.coerce.number().int().optional(),
  /** ERPID — filters to lines whose vendor is assigned to this ERP system
   * (erp_systems.name, e.g. "SAP_C11"), per §4.1.3 Input Fields. */
  erpId: z.string().optional(),
  /** ConfirmationStatus — §4.1.3 Input Fields; "all" replaces the legacy's
   * implicit always-unconfirmed default so the documented field is real. */
  confirmationStatus: z.enum(["all", "confirmed", "unconfirmed"]).optional().default("unconfirmed"),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
});
export type PoFilterInput = z.infer<typeof poFilterSchema>;

/** PO Confirmation Split — proposed change per line. Server-side enforces the
 * documented quantity-mismatch rule: sum of split quantities must equal
 * OriginalPOLineQuantity. */
export const poSplitLineSchema = z.object({
  poLineId: z.number().int(),
  proposedQty: z.number().positive(),
  proposedDeliveryDate: z.string(),
  reasonId: z.number().int(),
  comment: z.string().optional(),
});
export const poSplitSubmitSchema = z.object({
  lines: z.array(poSplitLineSchema).min(1),
});
export type PoSplitSubmitInput = z.infer<typeof poSplitSubmitSchema>;

export const poConfirmSelectSchema = z.object({
  poLineIds: z.array(z.number().int()).min(1),
});

export const poProposalDecisionSchema = z.object({
  proposalIds: z.array(z.number().int()).min(1),
  decision: z.enum(["approve", "reject"]),
  comment: z.string().optional(),
});

export const poCommentSchema = z.object({
  poLineId: z.number().int(),
  comment: z.string().min(1),
});

/** Supplier PO Proposal Input — §4.2.3. */
export const poProposalsFilterSchema = z.object({
  vendorId: z.coerce.number().int().optional(),
  plantId: z.coerce.number().int().optional(),
  materialNumber: z.string().optional(),
  poNumber: z.string().optional(),
  erpId: z.string().optional(),
});
export type PoProposalsFilterInput = z.infer<typeof poProposalsFilterSchema>;

/** Supplier PO Proposal History Report Input — §4.4. */
export const poProposalHistoryFilterSchema = poProposalsFilterSchema.extend({
  proposalStatus: z.enum(["all", ...PROPOSAL_STATUSES]).optional().default("all"),
});
export type PoProposalHistoryFilterInput = z.infer<typeof poProposalHistoryFilterSchema>;

/** Confirmed PO Report Input — §4.3. */
export const poReportFilterSchema = z.object({
  vendorId: z.coerce.number().int().optional(),
  plantId: z.coerce.number().int().optional(),
  materialNumber: z.string().optional(),
  fromDate: z.string().optional(),
  toDate: z.string().optional(),
  erpId: z.string().optional(),
});
export type PoReportFilterInput = z.infer<typeof poReportFilterSchema>;

/** PO Confirm Audit Log Input — §4.5 (no ERPID/DateFormat documented for this screen). */
export const poAuditLogFilterSchema = z.object({
  vendorId: z.coerce.number().int().optional(),
  plantId: z.coerce.number().int().optional(),
  materialNumber: z.string().optional(),
  fromDate: z.string().optional(),
  toDate: z.string().optional(),
});
export type PoAuditLogFilterInput = z.infer<typeof poAuditLogFilterSchema>;
